import { makeObservable, observable, override } from 'mobx';

import AppSettings from '../AppSettings';
import { IApiClient } from '../data/client';
import { GetPromotionForConfigurationCodeQuery } from '../data/model';
import type { GetPromotionForConfigurationCodeQueryResponse, RequestQuoteFormConfig } from '../data/model';
import { HubSpotPageTranslation } from '../localization/SiteTranslation';
import { IEventAggregator } from '../shared/EventAggregator';
import {
  HubSpotFormWithProductState,
  IHubSpotFormWithProductMemento,
} from '../shared/hubSpot/HubSpotFormWithProductState';
import { INavigationService } from '../shared/NavigationService';
import { StoreUrl } from '../shared/StoreUrl';
import { StoreState } from '../StoreState';
import { RequestQuoteSubmittedEvent } from './RequestQuotePageEvents';
import { RequestQuoteQuery } from './RequestQuoteQuery';
import { RequestQuoteQueryParser } from './RequestQuoteQueryParser';

export interface RequestQuoteFormData {
  consentGranted: boolean;
  quantity: number;
  countryName?: string;
  customerType?: 'Business' | 'Private';
}

export interface IRequestQuoteMemento extends IHubSpotFormWithProductMemento<RequestQuoteFormConfig> {
  promotionResponse?: GetPromotionForConfigurationCodeQueryResponse;
}

export class RequestQuoteState extends HubSpotFormWithProductState<
  RequestQuoteFormConfig,
  IRequestQuoteMemento,
  RequestQuoteQuery
> {
  private formSubmittedData: RequestQuoteFormData = {
    consentGranted: false,
    quantity: 1,
  };

  @observable.shallow
  promotionResponse?: GetPromotionForConfigurationCodeQueryResponse;

  private readonly eventAggregator: IEventAggregator;

  constructor(
    client: IApiClient,
    settings: AppSettings,
    navigation: INavigationService,
    pageTranslation: HubSpotPageTranslation,
    eventAggregator: IEventAggregator,
  ) {
    super(client, settings, navigation, pageTranslation);

    makeObservable(this);

    this.eventAggregator = eventAggregator;
  }

  protected parseQuery(rawQuery: Map<string, string>): RequestQuoteQuery {
    return RequestQuoteQueryParser.toModel(rawQuery);
  }

  setFormSubmittedData(data: RequestQuoteFormData) {
    this.formSubmittedData = data;
  }

  @override setFormSubmitted() {
    this.notifyRequestQuoteSubmitted();
    super.setFormSubmitted();
  }

  get isRequestProcessedByDealer() {
    return this.hubSpotConfig.isProcessedByDealer;
  }

  async onLoad(store: StoreState): Promise<void> {
    await super.onLoad(store);

    let promotionResponse: GetPromotionForConfigurationCodeQueryResponse = null;

    if (this.query.configurationCode) {
      promotionResponse = await this.client.send(
        new GetPromotionForConfigurationCodeQuery({
          configurationCode: this.query.configurationCode,
          campaignCode: this.query.campaignCode,
          shoppingContext: this.shoppingContext,
        }),
      );
    }

    this.initializeChild(promotionResponse);
  }

  protected initializeChild(promotionResponse: GetPromotionForConfigurationCodeQueryResponse) {
    this.promotionResponse = promotionResponse;
  }

  protected hubSpotConfigSelector(store: StoreState) {
    return store.storeResponse.storeContext.requestQuoteConfig;
  }

  protected ensureFeatureIsEnabled(store: StoreState) {
    if (!store.canRequestQuote()) {
      this.navigation.navigateTo(StoreUrl.homeUrl());
    }
  }

  getMemento(): IRequestQuoteMemento {
    const memento = super.getMemento();
    memento.promotionResponse = this.promotionResponse;
    return memento;
  }

  restoreMemento(memento: IRequestQuoteMemento) {
    this.promotionResponse = memento.promotionResponse;
    super.restoreMemento(memento);
  }

  get promotionDescription() {
    return this.promotionResponse?.promotionDescription;
  }

  private notifyRequestQuoteSubmitted() {
    this.eventAggregator.publish(
      new RequestQuoteSubmittedEvent(
        this.product,
        this.query.configurationCode,
        this.formSubmittedData,
        this.shoppingContext.currencyCode,
      ),
    );
  }
}
